import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  useTheme,
  CircularProgress,
  Chip,
  InputBase,
  Tooltip,
  MenuItem,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  PersonSearch as PersonSearchIcon,
  Search as SearchIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import SidebarPanelHeader from '../SidebarPanelHeader';
import { Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  useCampaignId,
  useSpreadsheetViewType
} from '../../../context/CampaignContext';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTargetPersonaSearch } from '../../../context/TargetPersonaSearchContext';
import PlanRestrictionContactModal from '../../modal/PlanRestrictionContactModal';
import {
  isEnterprisePlanRestrictionError,
  isEnterprisePlanRestrictionMessage
} from '../../../utils/planRestrictionContactModal';
import {
  getCampaignTargetPersona,
  putCampaignTargetPersona,
  startTargetPersonaFetch,
  streamTargetPersonaFetchResult,
  defaultTargetPersonaConfig,
  TARGET_PERSONA_LOCATION_OPTIONS,
  type TargetPersonaConfig,
  type TargetPersonaTargetLocation
} from '../../../api/campaignTargetPersona';

import { getCells } from '../../../api/spreadsheet';

/** Max visible account rows allowed per target persona search (after sheet filters). */
const TARGET_PERSONA_SEARCH_MAX_ROWS = 500;

interface TargetPersonaToolPanelProps {
  onClose: () => void;
  /** Row indices currently hidden by the sheet filter; persona search only runs on visible rows (max 500). */
  getFilteredOutRowIds?: () => number[] | null;
  /** When true, enrichment or prioritization locks the spreadsheet; persona search cannot be started. */
  spreadsheetJobLocked?: boolean;
  /** When true, enrichment only — enrichment-specific banner/snackbar only. */
  spreadsheetLockedDuringEnrichment?: boolean;
}

function targetLocationsEqual(
  a: TargetPersonaTargetLocation[],
  b: TargetPersonaTargetLocation[]
): boolean {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every((x) => setA.has(x));
}

function configsEqual(a: TargetPersonaConfig, b: TargetPersonaConfig): boolean {
  if (a.targetAudience !== b.targetAudience) return false;
  if (a.targetTitles.length !== b.targetTitles.length) return false;
  if (a.maxLeadsPerAccount !== b.maxLeadsPerAccount) return false;
  if (!targetLocationsEqual(a.targetLocations, b.targetLocations)) return false;
  return a.targetTitles.every((t, i) => t === b.targetTitles[i]);
}

const TargetPersonaToolPanel: React.FC<TargetPersonaToolPanelProps> = ({
  onClose,
  getFilteredOutRowIds,
  spreadsheetJobLocked,
  spreadsheetLockedDuringEnrichment = false
}) => {
  const campaignId = useCampaignId();
  const viewType = useSpreadsheetViewType();
  const { showSnackbar } = useSnackbar();
  const { isSearchRunning, setSearchRunning, notifySearchComplete } =
    useTargetPersonaSearch();
  const theme = useTheme();
  const mountedRef = useRef(true);

  const [config, setConfig] = useState<TargetPersonaConfig>(
    defaultTargetPersonaConfig()
  );
  const [initialConfig, setInitialConfig] = useState<TargetPersonaConfig>(
    defaultTargetPersonaConfig()
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchRunning, setFetchRunning] = useState(false);
  const [planRestrictionModalOpen, setPlanRestrictionModalOpen] =
    useState(false);
  const [planRestrictionDetail, setPlanRestrictionDetail] = useState<
    string | null
  >(null);
  const streamCancelRef = useRef<(() => void) | null>(null);

  const showPersonaPlanRestriction = (detailMessage: string) => {
    setPlanRestrictionDetail(detailMessage);
    setPlanRestrictionModalOpen(true);
  };

  const closePlanRestrictionModal = () => {
    setPlanRestrictionModalOpen(false);
    setPlanRestrictionDetail(null);
  };

  useEffect(() => {
    if (!campaignId) {
      const defaultConfig = defaultTargetPersonaConfig();
      setConfig(defaultConfig);
      setInitialConfig(defaultConfig);
      setTitleInput('');
      setIsEditMode(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getCampaignTargetPersona(campaignId)
      .then((loaded) => {
        if (cancelled) return;
        setConfig(loaded);
        setInitialConfig(loaded);
        const hasConfig =
          loaded.targetAudience.trim() !== '' || loaded.targetTitles.length > 0;
        setIsEditMode(!hasConfig);
      })
      .catch((err) => {
        if (cancelled) return;
        if (isEnterprisePlanRestrictionError(err)) {
          showPersonaPlanRestriction(
            err instanceof Error ? err.message : String(err)
          );
        } else {
          showSnackbar(
            `Failed to load target persona: ${err instanceof Error ? err.message : String(err)}`,
            'error'
          );
        }
        const defaultConfig = defaultTargetPersonaConfig();
        setConfig(defaultConfig);
        setInitialConfig(defaultConfig);
        setIsEditMode(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [campaignId, showSnackbar]);

  const setTargetAudience = useCallback((value: string) => {
    setConfig((prev) => ({ ...prev, targetAudience: value }));
  }, []);

  const [titleInput, setTitleInput] = useState('');

  const addTitlesFromInput = useCallback(
    (raw: string) => {
      const parts = raw
        .split(',')
        .map((p) => p.trim())
        .filter((p) => p !== '');
      if (parts.length === 0) return;
      const existingLower = new Set(
        config.targetTitles.map((t) => t.toLowerCase())
      );
      const newTitles: string[] = [];
      let hasDuplicate = false;
      for (const part of parts) {
        const lower = part.toLowerCase();
        if (existingLower.has(lower)) {
          hasDuplicate = true;
          continue;
        }
        existingLower.add(lower);
        newTitles.push(part);
      }
      if (newTitles.length > 0) {
        setConfig((prev) => ({
          ...prev,
          targetTitles: [...prev.targetTitles, ...newTitles]
        }));
      }
      if (hasDuplicate) {
        showSnackbar('Target title already added', 'warning');
      }
    },
    [config.targetTitles, setConfig, showSnackbar]
  );

  const handleTitleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (
        e.key === 'Backspace' &&
        titleInput.trim() === '' &&
        config.targetTitles.length > 0
      ) {
        e.preventDefault();
        setConfig((prev) => ({
          ...prev,
          targetTitles: prev.targetTitles.slice(
            0,
            Math.max(0, prev.targetTitles.length - 1)
          )
        }));
        return;
      }
      if (e.key !== 'Enter' && e.key !== ',') return;
      e.preventDefault();
      addTitlesFromInput(titleInput);
      setTitleInput('');
    },
    [titleInput, config.targetTitles.length, addTitlesFromInput, setConfig]
  );

  const handleRemoveTitle = useCallback((index: number) => {
    setConfig((prev) => ({
      ...prev,
      targetTitles: prev.targetTitles.filter((_, i) => i !== index)
    }));
  }, []);

  const handleSave = async () => {
    if (!campaignId) {
      showSnackbar('No campaign selected', 'error');
      return;
    }
    setSaving(true);
    try {
      const toSave: TargetPersonaConfig = {
        targetAudience: config.targetAudience.trim(),
        targetTitles: config.targetTitles
          .map((t) => t.trim())
          .filter((t) => t !== ''),
        maxLeadsPerAccount: Math.max(
          1,
          Math.min(500, Math.floor(config.maxLeadsPerAccount) || 10)
        ),
        targetLocations: [...config.targetLocations]
      };
      const saved = await putCampaignTargetPersona(campaignId, toSave);
      setConfig(saved);
      setInitialConfig(saved);
      setIsEditMode(false);
      showSnackbar('Target persona saved', 'success');
    } catch (err) {
      if (isEnterprisePlanRestrictionError(err)) {
        showPersonaPlanRestriction(
          err instanceof Error ? err.message : String(err)
        );
      } else {
        showSnackbar(
          `Failed to save: ${err instanceof Error ? err.message : String(err)}`,
          'error'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const hasPersonaConfig =
    config.targetAudience.trim() !== '' || config.targetTitles.length > 0;

  const handleSearchPersona = async () => {
    if (spreadsheetJobLocked) {
      showSnackbar(
        spreadsheetLockedDuringEnrichment
          ? 'Spreadsheet is locked during enrichment. Please wait for it to complete.'
          : 'Spreadsheet is locked during prioritization. Please wait for it to complete.',
        'info'
      );
      return;
    }
    if (!campaignId) {
      showSnackbar('No campaign selected', 'error');
      return;
    }
    if (hasChanged) {
      showSnackbar('Save your target persona first, then search', 'warning');
      return;
    }
    if (!hasPersonaConfig) {
      showSnackbar(
        'Add an ICP description and/or at least one job title first',
        'warning'
      );
      return;
    }

    setFetchRunning(true);
    setSearchRunning(true);
    showSnackbar(
      'Search running in background. You can close this panel—the job will continue.',
      'info'
    );
    try {
      const { cells } = await getCells(campaignId, viewType);
      let rowIds = Array.from(new Set(cells.map((c) => c.row_id))).sort(
        (a, b) => a - b
      );
      const filteredOut = getFilteredOutRowIds?.();
      if (Array.isArray(filteredOut) && filteredOut.length > 0) {
        const hiddenSet = new Set(filteredOut);
        rowIds = rowIds.filter((id) => !hiddenSet.has(id));
      }
      if (rowIds.length === 0) {
        showSnackbar(
          'No accounts to search. Add accounts to your campaign first.',
          'warning'
        );
        setFetchRunning(false);
        setSearchRunning(false);
        return;
      }
      if (rowIds.length > TARGET_PERSONA_SEARCH_MAX_ROWS) {
        showSnackbar(
          `Target persona search supports at most ${TARGET_PERSONA_SEARCH_MAX_ROWS} visible accounts (${rowIds.length} selected). Narrow your filter or reduce rows.`,
          'warning'
        );
        setFetchRunning(false);
        setSearchRunning(false);
        return;
      }

      const { workflow_id } = await startTargetPersonaFetch(campaignId, {
        row_ids: rowIds
      });

      streamCancelRef.current = streamTargetPersonaFetchResult(
        campaignId,
        workflow_id,
        {
          onStatus: (status, error) => {
            if (status === 'FAILED' && error) {
              if (isEnterprisePlanRestrictionMessage(error)) {
                showPersonaPlanRestriction(error);
              } else {
                showSnackbar(error, 'error');
              }
            }
            if (status === 'COMPLETED') {
              notifySearchComplete();
              showSnackbar(
                'Persona search complete! View results by clicking on account cells.',
                'success'
              );
            }
          },
          onComplete: () => {
            if (mountedRef.current) {
              setFetchRunning(false);
            }
            setSearchRunning(false);
          },
          onError: (message) => {
            if (isEnterprisePlanRestrictionMessage(message)) {
              showPersonaPlanRestriction(message);
            } else {
              showSnackbar(`Search persona failed: ${message}`, 'error');
            }
            if (mountedRef.current) {
              setFetchRunning(false);
            }
            setSearchRunning(false);
          }
        }
      );
    } catch (err) {
      if (isEnterprisePlanRestrictionError(err)) {
        showPersonaPlanRestriction(
          err instanceof Error ? err.message : String(err)
        );
      } else {
        showSnackbar(
          `Failed to start search: ${err instanceof Error ? err.message : String(err)}`,
          'error'
        );
      }
      setFetchRunning(false);
      setSearchRunning(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Do NOT cancel the stream on unmount - job continues in background.
      // streamCancelRef.current is intentionally not called here.
    };
  }, []);

  const handleEdit = () => setIsEditMode(true);
  const handleCancel = () => {
    setConfig(initialConfig);
    setTitleInput('');
    setIsEditMode(false);
  };
  const hasChanged = !configsEqual(config, initialConfig);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          position: 'relative'
        }}
      >
        <SidebarPanelHeader
          icon={<PersonSearchIcon />}
          title="Target Persona"
          infoTooltip="Describe your ideal buyer in words, then list job titles. Both shape who we find at each account."
          titleVariant="custom"
          actionButtons={[
            <IconButton
              key="close"
              size="small"
              onClick={onClose}
              aria-label="Close panel"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ]}
          sx={{ mb: 2 }}
        />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2
          }}
        >
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      </Box>
    );
  }

  const editButton = !isEditMode ? (
    <IconButton
      size="small"
      onClick={handleEdit}
      sx={{ color: 'text.secondary' }}
      title="Edit target persona"
      aria-label="Edit target persona"
    >
      <EditIcon fontSize="small" />
    </IconButton>
  ) : null;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          position: 'relative'
        }}
      >
        <SidebarPanelHeader
          icon={<PersonSearchIcon />}
          title="Target Persona"
          infoTooltip="Describe your ideal buyer in words, then list job titles. Both shape who we find at each account."
          titleVariant="custom"
          sx={{ mb: 2 }}
          actionButtons={[
            ...(editButton ? [editButton] : []),
            <IconButton
              key="close"
              size="small"
              onClick={onClose}
              aria-label="Close panel"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ]}
        />

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 3
          }}
        >
          {/* Ideal buyer (targetAudience) */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 0.5
              }}
            >
              Ideal buyer
            </Typography>
            <Typography
              id="target-persona-audience-desc"
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1.5, lineHeight: 1.4 }}
            >
              Who they are, not job titles. We use this to judge fit after
              reading their LinkedIn profiles.
            </Typography>
            <TextField
              id="target-persona-audience"
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
              placeholder="e.g. Leaders who own pipeline at B2B SaaS companies"
              value={config.targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              disabled={!isEditMode}
              inputProps={{
                'aria-describedby': 'target-persona-audience-desc'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Box>

          {/* Job titles (targetTitles) */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 0.5
              }}
            >
              Job titles
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1.5, lineHeight: 1.4 }}
            >
              Roles we look up at each account (e.g. VP Sales).
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                alignItems: 'center',
                minHeight: 40,
                maxHeight: 160,
                overflowY: 'auto',
                p: 1,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.default'
              }}
            >
              {config.targetTitles.map((title, index) => (
                <Chip
                  key={`${title}-${index}`}
                  label={title}
                  size="small"
                  onDelete={
                    isEditMode ? () => handleRemoveTitle(index) : undefined
                  }
                  sx={{ fontSize: '0.8125rem' }}
                />
              ))}
              {isEditMode && (
                <InputBase
                  value={titleInput}
                  onChange={(e) => setTitleInput(e.target.value)}
                  onKeyDown={handleTitleKeyDown}
                  placeholder="Add title, Enter or comma"
                  onBlur={() => {
                    addTitlesFromInput(titleInput);
                    setTitleInput('');
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 120,
                    fontSize: '0.8125rem',
                    '& input': { py: 0.5, px: 0.5 }
                  }}
                  inputProps={{ 'aria-label': 'Add job title' }}
                />
              )}
            </Box>
          </Box>

          {/* Target location (persona fetch) */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 0.5
              }}
            >
              Target location
            </Typography>
            <Typography
              id="target-persona-location-desc"
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1.5, lineHeight: 1.4 }}
            >
              Regions for matching leads by location. Choose one or more.
            </Typography>
            <TextField
              id="target-persona-location"
              select
              fullWidth
              size="small"
              value={config.targetLocations}
              onChange={(e) => {
                const raw = e.target.value;
                const arr: string[] = Array.isArray(raw)
                  ? raw
                  : typeof raw === 'string'
                    ? [raw]
                    : [];
                const val = arr.filter((x): x is TargetPersonaTargetLocation =>
                  (
                    TARGET_PERSONA_LOCATION_OPTIONS as readonly string[]
                  ).includes(x)
                );
                setConfig((prev) => ({ ...prev, targetLocations: val }));
              }}
              disabled={!isEditMode}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: (selected) => {
                  const s = selected as TargetPersonaTargetLocation[];
                  return s.length > 0 ? s.join(', ') : 'None selected';
                },
                MenuProps: {
                  PaperProps: { sx: { maxHeight: 280 } }
                }
              }}
              inputProps={{
                'aria-describedby': 'target-persona-location-desc'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            >
              {TARGET_PERSONA_LOCATION_OPTIONS.map(
                (loc: TargetPersonaTargetLocation) => (
                  <MenuItem key={loc} value={loc}>
                    <Checkbox
                      size="small"
                      checked={config.targetLocations.includes(loc)}
                      sx={{ mr: 0.5, py: 0 }}
                    />
                    <ListItemText
                      primary={loc}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </MenuItem>
                )
              )}
            </TextField>
          </Box>

          {/* Max leads */}
          <Box>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary
                }}
              >
                Max leads per account
              </Typography>
              <Tooltip
                title="We pull up to this many leads per account and research them to see if they match your target persona. The higher the cap, the more credits it will consume."
                arrow
                placement="top"
              >
                <IconButton
                  size="small"
                  sx={{ color: 'text.secondary', p: 0.25 }}
                  aria-label="Learn more about credits"
                >
                  <InfoIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              type="number"
              size="small"
              value={config.maxLeadsPerAccount}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!Number.isNaN(v) && v >= 1) {
                  setConfig((prev) => ({
                    ...prev,
                    maxLeadsPerAccount: Math.min(500, v)
                  }));
                } else if (e.target.value === '') {
                  setConfig((prev) => ({ ...prev, maxLeadsPerAccount: 10 }));
                }
              }}
              disabled={!isEditMode}
              inputProps={{ min: 1, max: 500 }}
              sx={{
                maxWidth: 120,
                '& .MuiInputBase-root': { borderRadius: 1.5 }
              }}
            />
          </Box>
        </Box>

        {isEditMode && (
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
              display: 'flex',
              gap: 1
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCancel}
              disabled={saving}
              sx={(t) => ({
                borderRadius: '50px',
                textTransform: 'capitalize',
                py: 1.5,
                border: `1px solid ${t.customColors?.themePrimary || 'primary.main'}`,
                color: t.customColors?.themePrimary || 'primary.main',
                fontWeight: 600,
                fontSize: '0.95rem'
              })}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSave}
              disabled={saving || !hasChanged}
              sx={{
                borderRadius: '50px',
                textTransform: 'capitalize',
                py: 1.5,
                backgroundColor:
                  theme.customColors?.themePrimary || 'primary.main',
                color: theme.customColors?.themeSecondary || 'white',
                fontWeight: 600,
                fontSize: '0.8125rem',
                '&:hover': {
                  backgroundColor:
                    theme.customColors?.themePrimary || 'primary.main',
                  opacity: 0.9,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 20px ${theme.customColors?.themePrimary || '#1976d2'}40`
                },
                transition: 'all 0.2s ease',
                '&.Mui-disabled': { opacity: 0.6 }
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        )}

        {/* Search leads CTA - at bottom in view mode */}
        {!isEditMode && (
          <Box
            sx={{
              mt: 'auto',
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              flexShrink: 0
            }}
          >
            {spreadsheetLockedDuringEnrichment && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Spreadsheet is locked during enrichment. Wait for it to complete
                before searching.
              </Typography>
            )}
            <Button
              variant="contained"
              fullWidth
              onClick={handleSearchPersona}
              disabled={
                !hasPersonaConfig ||
                hasChanged ||
                config.targetLocations.length === 0 ||
                fetchRunning ||
                isSearchRunning ||
                !!spreadsheetJobLocked
              }
              startIcon={
                fetchRunning || isSearchRunning ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SearchIcon sx={{ fontSize: 20 }} />
                )
              }
              sx={(t) => ({
                borderRadius: '50px',
                textTransform: 'capitalize',
                py: 1.5,
                fontWeight: 600,
                fontSize: '0.95rem',
                border: `1px solid ${t.customColors?.themePrimary || 'primary.main'}`,
                backgroundColor: t.customColors?.themePrimary || 'primary.main',
                color: t.customColors?.themeSecondary || 'white',
                '&:disabled': { borderColor: 'inherit', opacity: 0.6 }
              })}
            >
              Search persona
            </Button>
          </Box>
        )}
      </Box>
      <PlanRestrictionContactModal
        open={planRestrictionModalOpen}
        onClose={closePlanRestrictionModal}
        detail={planRestrictionDetail}
      />
    </>
  );
};

export default TargetPersonaToolPanel;
